<template>
  <div v-if="start">
    <div v-if="!currentScenario">
      Такого бота не существует
    </div>
    <div v-else class="lg:w-full">
      <vx-card class="mb-10">
        <div class="flex justify-between">
          <div class="vx-card__title mb-4">
            <h2 class="mb-4">Редактирование чат-бота «{{ currentScenario.name }}»</h2>
            <p>Настраивайте меню бота, добавляйте новые пункты, подпункты, медиафайлы, геометки и меняйте фразы.</p>
          </div>
          <div>
            <vs-button @click.prevent="handlePublish">Запустить</vs-button>
          </div>
        </div>
        <div class="vx-row mb-12">
          <div class="vx-col w-full lg:w-1/2 mb-6">
            <vs-input
              class="w-full"
              label="Название чат-бота: *"
              placeholder="Например, HAL 9000"
              name="name"
              v-model="currentSettings.name"
              v-validate="'required'"
              :class="isEditedClass(currentSettings.name, settings.name)"
            />
            <span class="text-danger text-sm" v-show="errors.has('name')">{{ errors.first('name') }}</span>
          </div>
          <div class="vx-col w-full lg:w-1/2 mb-6">
            <vs-input
              class="w-full"
              label="Описание чат-бота:"
              placeholder="Например, для лучшего клиентского сервиса"
              name="description"
              v-model="currentSettings.description"
              :class="isEditedClass(currentSettings.description, settings.description)"
            />
            <span class="text-danger text-sm" v-show="errors.has('description')">{{ errors.first('description') }}</span>
          </div>
        </div>
      </vx-card>

      <ScenarioSteps
        class="mb-10"
        :company-slug="authUserCompany.slug"
        :scenario-slug="currentScenario.slug"
      />

      <vx-card class="mb-10">
        <div class="vx-card__title mb-4">
          <h2 class="mb-4">Ответы по умолчанию</h2>
          <p>Задайте ответы, которые клиенты будут получать в зависимости от работы вашего центра.</p>
        </div>
        <div class="vx-row">
          <div class="vx-col w-full lg:w-1/2 mb-6">
            <TextareaWithLock
              v-model="currentSettings.settings['message.not_found']"
              label="Ответ бота при нераспознанном ответе клиента"
              placeholder="Я не понимаю, введите цифру из меню"
              :is-edited-class="isEditedClass(
                currentSettings.settings['message.not_found'], settings.settings['message.not_found']
              )"
            />
          </div>
          <div class="vx-col w-full lg:w-1/2 mb-6">
            <TextareaWithLock
              v-model="currentSettings.settings['message.manual_dialog']"
              label="Сообщение бота при переводе диалога на оператора"
              placeholder="Переключаем ваш диалог на оператора , ответим в ближайшее время"
              :is-edited-class="isEditedClass(
                currentSettings.settings['message.manual_dialog'], settings.settings['message.manual_dialog']
              )"
            />
          </div>
          <div class="vx-col w-full lg:w-1/2 mb-6">
            <TextareaWithLock
              v-model="currentSettings.settings['message.operator_unavailable']"
              label="Ответ при необработанном запросе клиента"
              placeholder="Мы свяжемся с вами"
              :is-edited-class="isEditedClass(
                currentSettings.settings['message.operator_unavailable'], settings.settings['message.operator_unavailable']
              )"
            />
          </div>
        </div>
      </vx-card>

      <vx-card class="mb-10">
        <div class="vx-card__title mb-10">
          <h2 class="mb-4">Дополнительные настройки</h2>
          <p>Усовершенствуйте вашего бота.</p>
        </div>
        <div class="vx-row">
          <div class="vx-col w-full lg:w-1/2 mb-6">
            <p>Максимальное количество нераспознанных сообщений: {{ currentSettings.settings['general.max_unrecognized_inputs'] }}</p>
            <vs-slider
              step="1"
              :min="1"
              :max="5"
              v-model="currentSettings.settings['general.max_unrecognized_inputs']"
              name="general_max_unrecognized_inputs"
              v-validate="'required'"
            />
          </div>
        </div>
        <div class="vx-row">
          <div class="vx-col w-full lg:w-1/2 mb-6">
            <div class="flex items-center">
              <div class="mr-4">Клиент может сам завершить чат</div>
              <vs-switch
                v-model="currentSettings.settings['client_close.enabled']"
                :class="isEditedClass(currentSettings.settings['client_close.enabled'], settings.settings['client_close.enabled'])"
              />
            </div>
          </div>
        </div>
        <div class="vx-row" v-if="currentSettings.settings['client_close.enabled']">
          <div class="vx-col w-full lg:w-1/2 mb-6">
            <vs-input
              class="w-full"
              label="Ключевое слово для завершения чата: *"
              placeholder="Например, стоп"
              name="client_close_keyword"
              v-model="currentSettings.settings['client_close.keyword']"
              v-validate="'required'"
              :class="isEditedClass(currentSettings.settings['client_close.keyword'], settings.settings['client_close.keyword'])"
            />
            <span class="text-danger text-sm" v-show="errors.has('client_close_keyword')">{{ errors.first('client_close_keyword') }}</span>
          </div>
          <div class="vx-col w-full lg:w-1/2 mb-6">
            <vs-input
              class="w-full"
              label="Название пункта для завершения бота: *"
              placeholder="Например, Остановить бота"
              name="client_close_title"
              v-model="currentSettings.settings['client_close.title']"
              v-validate="'required'"
              :class="isEditedClass(currentSettings.settings['client_close.title'], settings.settings['client_close.title'])"
            />
            <span class="text-danger text-sm" v-show="errors.has('client_close_title')">{{ errors.first('client_close_title') }}</span>
          </div>
          <div class="vx-col w-full lg:w-1/2 mb-6">
            <vs-textarea
              label="Сообщение после завершения чата: *"
              placeholder="Например, До свидания! Ждем вас снова :)"
              rows="5"
              v-model="currentSettings.settings['client_close.message']"
              name="client_close_message"
              v-validate="'required'"
              :class="isEditedClass(currentSettings.settings['client_close.message'], settings.settings['client_close.message'])"
            />
            <span class="text-danger text-sm" v-show="errors.has('client_close_message')">{{ errors.first('client_close_message') }}</span>
          </div>
        </div>
        <div class="vx-row">
          <div class="vx-col w-full lg:w-1/2 mb-6">
            <div class="flex items-center">
              <div class="mr-4">Клиент может самостоятельно позвать оператора</div>
              <vs-switch
                v-model="currentSettings.settings['manual_dialog_switch.enabled']"
                :class="isEditedClass(currentSettings.settings['manual_dialog_switch.enabled'], settings.settings['manual_dialog_switch.enabled'])"
              />
            </div>
          </div>
        </div>
        <div class="vx-row" v-if="currentSettings.settings['manual_dialog_switch.enabled']">
          <div class="vx-col w-full lg:w-1/2 mb-6">
            <vs-input
              class="w-full"
              label="Ключевое слово для перевода переписки:"
              placeholder="Например, менеджер"
              name="manual_dialog_switch_keyword"
              type="text"
              v-validate="'required'"
              v-model="currentSettings.settings['manual_dialog_switch.keyword']"
              :class="isEditedClass(currentSettings.settings['manual_dialog_switch.keyword'], settings.settings['manual_dialog_switch.keyword'])"
            />
            <span class="text-danger text-sm" v-show="errors.has('manual_dialog_switch_keyword')">{{ errors.first('manual_dialog_switch_keyword') }}</span>
          </div>
          <div class="vx-col w-full lg:w-1/2 mb-6">
            <vs-input
              class="w-full"
              label="Название пункта в меню:"
              placeholder="Например, позвать менеджера"
              name="manual_dialog_switch_title"
              type="text"
              v-validate="'required'"
              v-model="currentSettings.settings['manual_dialog_switch.title']"
              :class="isEditedClass(currentSettings.settings['manual_dialog_switch.title'], settings.settings['manual_dialog_switch.title'])"
            />
            <span class="text-danger text-sm" v-show="errors.has('manual_dialog_switch_title')">{{ errors.first('manual_dialog_switch_title') }}</span>
          </div>
          <div class="vx-col w-full lg:w-1/2 mb-6">
            <vs-input
              class="w-full"
              label="Ответное сообщение:"
              placeholder="Например, скоро ответим вам"
              name="manual_dialog_switch_message"
              type="text"
              v-validate="'required'"
              v-model="currentSettings.settings['manual_dialog_switch.message']"
              :class="isEditedClass(currentSettings.settings['manual_dialog_switch.message'], settings.settings['manual_dialog_switch.message'])"
            />
            <span class="text-danger text-sm" v-show="errors.has('manual_dialog_switch_message')">{{ errors.first('manual_dialog_switch_message') }}</span>
          </div>
        </div>
      </vx-card>

      <SaveBlock
        :show="hasChanges"
        :loading="loading"
        @accept="saveSettings"
        @cancel="cancelChanges"
      />
    </div>
  </div>
</template>

<script>
import Scenarios from '../../api/http/models/Scenarios'
import ScenarioVersion from '../../api/http/models/ScenarioVersion'

import { mapGetters } from 'vuex'
import { isEditedClass, copyObject, compareObjects } from '../../helpers/helpers'

import ScenarioSteps from './components/ScenarioSteps'
import TextareaWithLock from '../../components/TextareaWithLock'

export default {
  data () {
    return {
      start: false,
      loading: false,
      currentScenario: null,
      settings: {
        name: '',
        description: '',
        settings: {
          'general.max_unrecognized_inputs': null,
          'client_close.enabled': false,
          'client_close.keyword': null,
          'client_close.title': null,
          'client_close.message': null,
          'manual_dialog_switch.enabled': false,
          'manual_dialog_switch.keyword': null,
          'manual_dialog_switch.title': null,
          'manual_dialog_switch.message': null,
          'message.not_found': null,
          'message.manual_dialog': null,
          'message.operator_unavailable': null
        }
      },
      currentSettings: {},
      hasChanges: false
    }
  },
  components: {
    ScenarioSteps,
    TextareaWithLock
  },
  computed: mapGetters(['authUserCompany']),
  watch: {
    currentSettings: {
      deep: true,
      handler (val) {
        this.hasChanges = !compareObjects(val, this.settings)
      }
    }
  },
  async created () {
    const response = await Scenarios.getOne(this.authUserCompany.slug, this.$route.params.scenarioSlug)

    if (response.data) {
      await this.setData(response.data)
      await this.copySettings()
    }
    this.start = true
  },
  methods: {
    isEditedClass,
    setData (data) {
      this.currentScenario      = data
      this.settings.name        = this.currentScenario.name
      this.settings.description = this.currentScenario.description
      if (this.currentScenario.settings) {
        this.settings.settings['general.max_unrecognized_inputs'] = this.currentScenario.settings['general.max_unrecognized_inputs']
        this.settings.settings['client_close.enabled'] = this.currentScenario.settings['client_close.enabled']
        this.settings.settings['client_close.keyword'] = this.currentScenario.settings['client_close.keyword']
        this.settings.settings['client_close.title'] = this.currentScenario.settings['client_close.title']
        this.settings.settings['client_close.message'] = this.currentScenario.settings['client_close.message']
        this.settings.settings['manual_dialog_switch.enabled'] = this.currentScenario.settings['manual_dialog_switch.enabled']
        this.settings.settings['manual_dialog_switch.keyword'] = this.currentScenario.settings['manual_dialog_switch.keyword']
        this.settings.settings['manual_dialog_switch.title'] = this.currentScenario.settings['manual_dialog_switch.title']
        this.settings.settings['manual_dialog_switch.message'] = this.currentScenario.settings['manual_dialog_switch.message']
        this.settings.settings['message.not_found'] = this.currentScenario.settings['message.not_found']
        this.settings.settings['message.manual_dialog'] = this.currentScenario.settings['message.manual_dialog']
        this.settings.settings['message.operator_unavailable'] = this.currentScenario.settings['message.operator_unavailable']
      }
    },
    copySettings () {
      this.currentSettings = copyObject(this.settings)
    },
    async saveSettings () {
      const validate = await this.$validator.validateAll()

      if (validate) {
        this.loading = true

        const response = await Scenarios.updateOne(
          this.authUserCompany.slug,
          this.currentScenario.slug,
          {
            name: this.currentSettings.name,
            description: this.currentSettings.description,
            settings: {
              'general.max_unrecognized_inputs': this.currentSettings.settings['general.max_unrecognized_inputs'],
              'client_close.enabled': this.currentSettings.settings['client_close.enabled'],
              'client_close.keyword': this.currentSettings.settings['client_close.keyword'],
              'client_close.title': this.currentSettings.settings['client_close.title'],
              'client_close.message': this.currentSettings.settings['client_close.message'],
              'manual_dialog_switch.enabled': this.currentSettings.settings['manual_dialog_switch.enabled'],
              'manual_dialog_switch.keyword': this.currentSettings.settings['manual_dialog_switch.keyword'],
              'manual_dialog_switch.title': this.currentSettings.settings['manual_dialog_switch.title'],
              'manual_dialog_switch.message': this.currentSettings.settings['manual_dialog_switch.message'],
              'message.not_found': this.currentSettings.settings['message.not_found'],
              'message.manual_dialog': this.currentSettings.settings['message.manual_dialog'],
              'message.operator_unavailable': this.currentSettings.settings['message.operator_unavailable']
            }
          }
        )

        if (response.status === 'success' && response.data) {
          await this.setData(response.data)
          await this.copySettings()
          this.$vs.notify({
            color: 'success',
            title: 'Настройки бота успешно обновлены',
            text: ''
          })
        } else {
          this.$vs.notify({
            color: 'danger',
            title: 'При сохранении произошла ошибка',
            text: ''
          })
        }

        this.loading = false
      }
    },
    cancelChanges () {
      this.copySettings()
    },
    async handlePublish () {
      const publish = async () => {
        const response = await ScenarioVersion.publish(this.authUserCompany.slug, this.currentScenario.slug)

        if (response.status === 'success') {
          this.$vs.notify({
            color: 'success',
            title: 'Сценарий успешно опубликован',
            text: `Новая версия сценария - ${response.data.version}`
          })
        } else if (response.status === 'no_changes') {
          this.$vs.notify({
            color: 'warning',
            title: 'Нет изменений',
            text: 'В сценарии нет изменений, поэтому публиковать новую версию бессмысленно'
          })
        } else {
          this.$vs.notify({
            color: 'danger',
            title: 'Произошла ошибка',
            text: ''
          })
        }
      }

      this.$vs.dialog({
        type: 'confirm',
        color: 'warning',
        title: 'Запустить бота?',
        text: 'При нажатии «Запустить» вы создадите новую версию сценария и запустите в работу',
        accept: publish,
        acceptText: 'Запустить',
        cancelText: 'Отмена'
      })
    }
  }
}
</script>
